import React, { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { AssetIcon } from '../../assets';
import FilterChip from './FilterChip';
import SearchField from './SearchField';
import FilterDropdown from './FilterDropdown';
import { ModuleFilterContext } from './ModuleFilterContext';
import { isFilterActive, mergeFilterGroups } from '../../data/moduleFilters';
import PopUpInfos from '../PopUpInfos';
import { PageFeedbackContext, PageNotice } from '../PageFeedback';

function renderLegacyFilters({ chips, search, trailingControls }) {
  return (
    <div className="wt-filter-bar wt-filter-bar--legacy">
      <div className="wt-filter-bar__chips">
        {chips.map((chip) => (
          <FilterChip key={chip.id || chip.label} {...chip} />
        ))}
      </div>
      {search ? (
        <div className="wt-filter-bar__search">
          <SearchField {...search} />
        </div>
      ) : null}
      {trailingControls ? <div className="wt-filter-bar__trailing">{trailingControls}</div> : null}
    </div>
  );
}

export default function FilterBar({
  config,
  model: providedModel,
  values = {},
  hasActiveFilters,
  onChange,
  onReset,
  resetLabel = 'Reset',
  filtersLabel = 'Filtri',
  chips = [],
  search = null,
  trailingControls = null,
  toolbarContainer = null,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const contextModel = useContext(ModuleFilterContext);
  const pageFeedback = useContext(PageFeedbackContext);
  const model = providedModel || contextModel;
  const generatedGroups = model?.groups || [];
  const mergedValues = { ...values, ...model?.values };
  const change = (key, value) => key.startsWith('form:') ? model?.onChange(key, value) : onChange?.(key, value);
  const reset = () => { model?.onReset(); onReset?.(); };

  if (!config && !model) {
    return renderLegacyFilters({ chips, search, trailingControls });
  }

  const searchConfig = config?.search;
  const groups = mergeFilterGroups(config?.groups || [], generatedGroups);
  const hasComputedActiveFilters = Boolean(hasActiveFilters || model?.hasActiveFilters || (searchConfig && values[searchConfig.key]) || groups.some((group) => isFilterActive(mergedValues[group.key], group)));

  const toolbar = (
      <div className="wt-filter-system__toolbar">
        {searchConfig ? (
          <SearchField
            value={values[searchConfig.key] ?? ''}
            onChange={(nextValue) => onChange?.(searchConfig.key, nextValue)}
            placeholder={searchConfig.placeholder || 'Cerca...'}
            label={searchConfig.label || searchConfig.placeholder || 'Cerca'}
            className="wt-filter-system__search"
          />
        ) : <span />}
        <div className="wt-filter-system__actions">
          <PopUpInfos label={filtersLabel}><button
            type="button"
            className={[
              'wt-filter-system__icon-button',
              'wt-filter-system__icon-button--filter',
              filtersOpen ? 'is-open' : '',
              hasComputedActiveFilters ? 'is-active' : '',
            ].filter(Boolean).join(' ')}
            onClick={() => setFiltersOpen((current) => !current)}
            aria-label={filtersLabel}
            aria-expanded={filtersOpen}
          >
            <AssetIcon name="actions.filters" className="wt-filter-system__icon" size={32} />
          </button></PopUpInfos>
          <PopUpInfos label={resetLabel}><button
            type="button"
            className="wt-filter-system__icon-button"
            onClick={reset}
            aria-label={resetLabel}
          >
            <AssetIcon name="actions.reset" className="wt-filter-system__icon" size={32} />
          </button></PopUpInfos>
        </div>
      </div>
  );

  return (
    <section className="wt-filter-system" data-page-section="filters" aria-label={config?.label || 'Filtri'}>
      {toolbarContainer ? createPortal(toolbar, toolbarContainer) : toolbar}

      {model?.error && model.error !== pageFeedback?.filterError ? <PageNotice>{model.error}</PageNotice> : null}
      {groups.length > 0 && filtersOpen ? (
        <div className="wt-filter-system__panel">
          {groups.map((group) => <FilterDropdown key={group.key} group={group} value={mergedValues[group.key]} onChange={(value) => change(group.key, value)} />)}
        </div>
      ) : null}
    </section>
  );
}
